import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import {
  invalidateInvoiceQueries,
  invalidateOverheadsQueries,
} from '@/modules/common/utils/query-invalidation';

import { deleteInvoice } from '../../api';
import { InvoiceListItem } from '../../types';

interface DeleteInvoiceModalProps {
  invoice: InvoiceListItem | null;
  onClose: () => void;
}

export const DeleteInvoiceModal = ({ onClose, invoice }: DeleteInvoiceModalProps) => {
  const { t } = useTranslation('authenticated.invoices', { keyPrefix: 'modals.deleteInvoice' });

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: deleteInvoice,
    onError: () => {},
    onSuccess: () => {
      void invalidateInvoiceQueries(queryClient);
      // Deleting an invoice reverts its linked income overheads to Pending.
      void invalidateOverheadsQueries(queryClient);
      onClose();
    },
  });

  if (!invoice) return null;

  return (
    <ConfirmationModal
      title={t('title')}
      hideCloseIcon
      message={
        <div>
          <Trans
            t={t}
            i18nKey="description"
            values={{
              invoiceNumber: '#' + invoice.number,
            }}
            components={{ bold: <strong />, br: <br /> }}
          />
          <Alert variant={'warning'} className="mt-2">
            <AlertTriangle className="h-5 w-5" />
            <AlertDescription>{t('warning')}</AlertDescription>
          </Alert>
        </div>
      }
      confirmText={t('submit')}
      open={!!invoice}
      isLoading={isPending}
      onClose={onClose}
      onConfirm={() => mutate({ invoiceId: invoice.id })}
    />
  );
};
